/**
 * DealBriefView — renders a rich structured AI Deal Brief payload.
 */
import React from 'react';
import {
  FileText,
  AlertTriangle,
  DollarSign,
  PieChart,
  Clock,
  Users,
  CheckCircle2,
  Target,
  TrendingUp,
} from 'lucide-react';
import { DealBrief } from '../types/dealBrief';

interface Props {
  brief: DealBrief;
}

const formatUSD = (n?: number): string => {
  if (n === undefined || n === null || Number.isNaN(n)) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
};

const severityBadge = (sev: string): string => {
  switch ((sev || '').toLowerCase()) {
    case 'high':
      return 'bg-red-100 text-red-700 border-red-200';
    case 'medium':
      return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    case 'low':
      return 'bg-gray-100 text-gray-700 border-gray-200';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200';
  }
};

const influenceBadge = (inf: string): string => {
  switch ((inf || '').toLowerCase()) {
    case 'high':
      return 'bg-purple-100 text-purple-700';
    case 'medium':
      return 'bg-blue-100 text-blue-700';
    case 'low':
      return 'bg-gray-100 text-gray-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const stanceBadge = (stance: string): string => {
  switch ((stance || '').toLowerCase()) {
    case 'champion':
      return 'bg-green-100 text-green-700';
    case 'skeptic':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

// Palette for budget-allocation bar segments.
const BAR_COLORS = [
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#14b8a6',
  '#ec4899',
  '#6366f1',
];

const DealBriefView: React.FC<Props> = ({ brief }) => {
  const bj = brief.brief_json || {};

  return (
    <div className="space-y-6">
      {/* Metadata */}
      <div className="text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
        <span>Version {brief.version}</span>
        {brief.generated_at && (
          <span>
            Generated {new Date(brief.generated_at).toLocaleString()}
          </span>
        )}
        {brief.model_used && <span>Model: {brief.model_used}</span>}
      </div>

      {/* Executive Summary */}
      {bj.executive_summary && (
        <section className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-500" />
            Executive Summary
          </h3>
          <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
            {bj.executive_summary}
          </p>
        </section>
      )}

      {/* Fit Assessment */}
      {bj.fit_assessment && (
        <section className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Target className="w-4 h-4 text-green-600" />
            Fit Assessment
          </h3>
          {bj.fit_assessment.why_this_works && (
            <p className="text-sm text-gray-800 whitespace-pre-wrap mb-4 leading-relaxed">
              {bj.fit_assessment.why_this_works}
            </p>
          )}
          {bj.fit_assessment.alignment_highlights &&
            bj.fit_assessment.alignment_highlights.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {bj.fit_assessment.alignment_highlights.map((h, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2 p-3 bg-green-50 border border-green-100 rounded-lg"
                  >
                    <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-800">{h}</span>
                  </div>
                ))}
              </div>
            )}
        </section>
      )}

      {/* Success Strategy */}
      {bj.success_strategy && (
        <section className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-blue-500" />
            Success Strategy
          </h3>
          {bj.success_strategy.approach && (
            <p className="text-sm text-gray-800 mb-4">
              {bj.success_strategy.approach}
            </p>
          )}
          {bj.success_strategy.opening_moves &&
            bj.success_strategy.opening_moves.length > 0 && (
              <div className="mb-4">
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                  Opening Moves
                </div>
                <ol className="space-y-2 text-sm text-gray-800 list-decimal list-inside">
                  {bj.success_strategy.opening_moves.map((m, i) => (
                    <li key={i}>{m}</li>
                  ))}
                </ol>
              </div>
            )}
          {bj.success_strategy.closing_signals_to_watch_for &&
            bj.success_strategy.closing_signals_to_watch_for.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                  Closing Signals to Watch For
                </div>
                <ul className="space-y-1 text-sm text-gray-800">
                  {bj.success_strategy.closing_signals_to_watch_for.map(
                    (s, i) => (
                      <li key={i} className="flex items-start">
                        <span className="mr-2 text-blue-500">▸</span>
                        <span>{s}</span>
                      </li>
                    ),
                  )}
                </ul>
              </div>
            )}
        </section>
      )}

      {/* Threats */}
      {bj.threats && bj.threats.length > 0 && (
        <section className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            Threats &amp; Mitigations
          </h3>
          <div className="space-y-3">
            {bj.threats.map((t, i) => (
              <div
                key={i}
                className="border-l-4 border-red-400 bg-red-50 p-4 rounded-r-lg"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="text-sm font-medium text-gray-900">
                    {t.threat}
                  </div>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full border font-medium ${severityBadge(
                      t.severity,
                    )}`}
                  >
                    {t.severity}
                  </span>
                </div>
                {t.mitigation && (
                  <div className="mt-2 text-sm text-gray-700">
                    <span className="font-medium">Mitigation:</span>{' '}
                    {t.mitigation}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Revenue Opportunities */}
      {bj.revenue_opportunities && (
        <section className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-green-600" />
            Revenue Opportunities
          </h3>
          {bj.revenue_opportunities.primary_revenue && (
            <div className="mb-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-baseline justify-between mb-1">
                <span className="text-xs uppercase tracking-wide text-green-700">
                  Primary
                </span>
                <span className="text-lg font-bold text-green-700">
                  {formatUSD(
                    bj.revenue_opportunities.primary_revenue.ballpark_annual_usd,
                  )}
                  <span className="text-xs font-normal text-gray-500 ml-1">
                    / yr
                  </span>
                </span>
              </div>
              <div className="text-sm text-gray-800">
                {bj.revenue_opportunities.primary_revenue.description}
              </div>
            </div>
          )}
          {bj.revenue_opportunities.secondary_revenue &&
            bj.revenue_opportunities.secondary_revenue.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                {bj.revenue_opportunities.secondary_revenue.map((r, i) => (
                  <div
                    key={i}
                    className="p-3 bg-gray-50 border border-gray-200 rounded-lg"
                  >
                    <div className="text-sm font-semibold text-gray-900">
                      {formatUSD(r.ballpark_annual_usd)}
                      <span className="text-xs font-normal text-gray-500 ml-1">
                        / yr
                      </span>
                    </div>
                    <div className="text-sm text-gray-700 mt-1">
                      {r.description}
                    </div>
                  </div>
                ))}
              </div>
            )}
          {bj.revenue_opportunities.notes && (
            <p className="text-sm text-gray-600 italic">
              {bj.revenue_opportunities.notes}
            </p>
          )}
        </section>
      )}

      {/* Budget Allocation */}
      {bj.budget_allocation && (
        <section className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <PieChart className="w-4 h-4 text-purple-600" />
            Budget Allocation
          </h3>
          <div className="flex items-baseline gap-4 mb-4">
            <div className="text-2xl font-bold text-gray-900">
              {formatUSD(bj.budget_allocation.total_money_to_raise_usd)}
            </div>
            <div className="text-xs text-gray-500">
              total raise target
              {bj.budget_allocation.runway_target_months
                ? ` • ${bj.budget_allocation.runway_target_months}mo runway`
                : ''}
            </div>
          </div>

          {bj.budget_allocation.allocation_breakdown &&
            bj.budget_allocation.allocation_breakdown.length > 0 && (
              <>
                {/* Stacked bar */}
                <div className="flex h-4 rounded-full overflow-hidden border border-gray-200 mb-3">
                  {bj.budget_allocation.allocation_breakdown.map((line, i) => (
                    <div
                      key={i}
                      style={{
                        width: `${Math.max(0, line.percent)}%`,
                        backgroundColor: BAR_COLORS[i % BAR_COLORS.length],
                      }}
                      title={`${line.category}: ${line.percent}%`}
                    />
                  ))}
                </div>
                <div className="space-y-2">
                  {bj.budget_allocation.allocation_breakdown.map((line, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-3 text-sm"
                    >
                      <div
                        className="w-3 h-3 rounded-sm mt-1 flex-shrink-0"
                        style={{
                          backgroundColor: BAR_COLORS[i % BAR_COLORS.length],
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between">
                          <span className="font-medium text-gray-900">
                            {line.category}
                          </span>
                          <span className="text-gray-700">
                            {line.percent}% · {formatUSD(line.amount_usd)}
                          </span>
                        </div>
                        {line.rationale && (
                          <div className="text-xs text-gray-600 mt-0.5">
                            {line.rationale}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
        </section>
      )}

      {/* Money to Raise */}
      {bj.money_to_raise && (
        <section className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-blue-600" />
            Money to Raise
          </h3>
          <div className="flex flex-wrap items-center gap-4">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="text-xs uppercase tracking-wide text-blue-700">
                This round
              </div>
              <div className="text-xl font-bold text-blue-900">
                {formatUSD(bj.money_to_raise.this_round_usd)}
              </div>
            </div>
            {bj.money_to_raise.future_round_schedule &&
              bj.money_to_raise.future_round_schedule.map((r, i) => (
                <React.Fragment key={i}>
                  <span className="text-gray-300 text-lg">→</span>
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="text-xs uppercase tracking-wide text-gray-500">
                      {r.name}{' '}
                      <span className="normal-case text-gray-400">
                        (+{r.timing_months_out}mo)
                      </span>
                    </div>
                    <div className="text-xl font-bold text-gray-900">
                      {formatUSD(r.target_usd)}
                    </div>
                  </div>
                </React.Fragment>
              ))}
          </div>
        </section>
      )}

      {/* Action Plan */}
      {bj.action_plan && (
        <section className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-orange-500" />
            Action Plan
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                label: 'Next 7 days',
                items: bj.action_plan.next_7_days || [],
                color: 'border-red-200 bg-red-50',
              },
              {
                label: 'Next 30 days',
                items: bj.action_plan.next_30_days || [],
                color: 'border-yellow-200 bg-yellow-50',
              },
              {
                label: 'Next 90 days',
                items: bj.action_plan.next_90_days || [],
                color: 'border-blue-200 bg-blue-50',
              },
            ].map((col, i) => (
              <div
                key={i}
                className={`p-4 border rounded-lg ${col.color}`}
              >
                <div className="text-xs uppercase tracking-wide text-gray-700 font-semibold mb-2">
                  {col.label}
                </div>
                {col.items.length > 0 ? (
                  <ul className="space-y-1.5 text-sm text-gray-800">
                    {col.items.map((it, j) => (
                      <li key={j} className="flex items-start">
                        <span className="mr-2 text-gray-500">•</span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-xs text-gray-500 italic">No items</div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Key Stakeholders */}
      {bj.key_stakeholders && bj.key_stakeholders.length > 0 && (
        <section className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-indigo-500" />
            Key Stakeholders
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {bj.key_stakeholders.map((s, i) => (
              <div
                key={i}
                className="p-3 border border-gray-200 rounded-lg bg-gray-50"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center flex-shrink-0 font-semibold text-sm">
                    {(s.name || '?').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {s.name}
                    </div>
                    {s.role && (
                      <div className="text-xs text-gray-600 truncate">
                        {s.role}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-2 flex gap-1.5">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${influenceBadge(
                      s.influence,
                    )}`}
                  >
                    {s.influence} influence
                  </span>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${stanceBadge(
                      s.stance,
                    )}`}
                  >
                    {s.stance}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default DealBriefView;
